/**
 * Graph Explorer - Constants Module
 * Shared constants used across all explorer modules
 * 
 * This file is the single source of truth for:
 * - Tab definitions
 * - Toolbar button configurations
 * - Window configurations
 * - Operators
 * - Algorithm categories
 * - Function categories
 * - Color palettes
 */

(function(global) {
    'use strict';

    // ============================================
    // Tab Definitions
    // ============================================
    const TABS = [
        { id: 'graph', label: 'Graph', icon: 'share-2' },
        { id: 'bookmarks', label: 'Bookmarks', icon: 'bookmark' },
        { id: 'timeline', label: 'Timeline', icon: 'clock' },
        { id: 'analytics', label: 'Analytics', icon: 'bar-chart-2' },
        { id: 'artefacts', label: 'Artefacts', icon: 'file-text' }
    ];

    // ============================================
    // Toolbar Button Definitions
    // ============================================
    const TOOLBAR_BUTTONS = {
        base: [
            { id: 'theme', icon: 'moon', tooltip: 'Toggle theme', shortcut: 'T', toggle: true }
            // Reset button moved to cursor toolbar in explorer-tasks.js
        ],
        display: [
            { id: 'physics', icon: 'zap', tooltip: 'Physics', shortcut: 'P', toggle: true },
            { id: 'layout', icon: 'layout-grid', tooltip: 'Improve layout', shortcut: 'I' },
            { id: 'fit', icon: 'maximize-2', tooltip: 'Fit to screen', shortcut: 'F' }
        ],
        appearance: [
            { id: 'settings', icon: 'sliders-horizontal', tooltip: 'Settings', shortcut: 'C', toggle: true, window: 'settings' }
        ],
        windows: [
            { id: 'explorer', icon: 'text-search', tooltip: 'Search & Find', shortcut: 'Ctrl+K', toggle: true, window: 'explorer' },
            { id: 'aliases', icon: 'link-2', tooltip: 'Aliases', shortcut: 'A', toggle: true, window: 'aliases' },
            { id: 'selection', icon: 'mouse-pointer-click', tooltip: 'Selection & Operators', shortcut: 'S', toggle: true, window: 'selection' },
            { id: 'algorithms', icon: 'cpu', tooltip: 'Algorithms', shortcut: 'K', toggle: true, window: 'algorithms' },
            { id: 'functions', icon: 'lightbulb', tooltip: 'Functions', shortcut: 'N', toggle: true, window: 'functions' },
            { id: 'documents', icon: 'file-text', tooltip: 'Documents', shortcut: 'D', toggle: true, window: 'documents' },
            { id: 'legend', icon: 'palette', tooltip: 'Legend', shortcut: 'G', toggle: true, window: 'legend' },
            { id: 'fql', icon: 'code', tooltip: 'FQL Editor', shortcut: 'Q', toggle: true, window: 'fql' }
        ],
        data: [
            { id: 'reload', icon: 'refresh-cw', tooltip: 'Reload data', shortcut: 'Shift+R' },
            { id: 'export', icon: 'download', tooltip: 'Export', shortcut: 'Ctrl+E' }
        ]
    };

    // ============================================
    // Window Configurations
    // ============================================
    const WINDOW_CONFIGS = {
        explorer: { title: 'Search & Find', icon: 'text-search', width: 380, height: 500 },
        settings: { title: 'Settings', icon: 'sliders-horizontal', width: 320, height: 480 },
        aliases: { title: 'Aliases', icon: 'link-2', width: 320, height: 400 },
        selection: { title: 'Selection & Operators', icon: 'mouse-pointer-click', width: 350, height: 550 },
        legend: { title: 'Legend', icon: 'palette', width: 300, height: 400 },
        algorithms: { title: 'Algorithms', icon: 'cpu', width: 340, height: 520 },
        functions: { title: 'Functions', icon: 'lightbulb', width: 360, height: 520 },
        documents: { title: 'Documents', icon: 'file-text', width: 320, height: 450 },
        fql: { title: 'FQL Editor', icon: 'code', width: 450, height: 520 }
    };

    // ============================================
    // Operators for Graph Manipulation
    // (Note: Find and Search have been moved to the Explorer window)
    // ============================================
    const OPERATORS = [
        { id: 'extend', icon: 'expand', label: 'Extend', description: 'Extend from selection', color: '#888888', requiresSelection: true },
        { id: 'connect', icon: 'git-branch', label: 'Connect', description: 'Find connections', color: '#22c55e', requiresSelection: true },
        { id: 'map', icon: 'map', label: 'Map', description: 'Map to taxonomy', color: '#888888', requiresSelection: true },
        { id: 'intersect', icon: 'layers', label: 'Intersect', description: 'Intersection of selections', color: '#888888', requiresSelection: true },
        { id: 'differs', icon: 'diff', label: 'Differs', description: 'Differences between selections', color: '#ec4899', requiresSelection: true },
        { id: 'union', icon: 'merge', label: 'Union', description: 'Union of selections', color: '#06b6d4', requiresSelection: true },
        { id: 'isolate', icon: 'scissors', label: 'Isolate', description: 'Isolate selection', color: '#f59e0b', requiresSelection: true },
        { id: 'remove', icon: 'trash-2', label: 'Remove', description: 'Remove from graph', color: '#ef4444', requiresSelection: true }
    ];

    // ============================================
    // Algorithm Categories
    // ============================================
    const ALGORITHM_CATEGORIES = [
        {
            id: 'centrality', name: 'Centrality', icon: 'bar-chart', description: 'Find important nodes',
            algorithms: [
                { id: 'pagerank', name: 'PageRank', description: 'Find influential nodes', formula: 'CENTRALITY.PAGERANK' },
                { id: 'degree', name: 'Degree', description: 'Find hubs (most connections)', formula: 'CENTRALITY.DEGREE' },
                { id: 'betweenness', name: 'Betweenness', description: 'Find bridge nodes', formula: 'CENTRALITY.BETWEENNESS' },
                { id: 'closeness', name: 'Closeness', description: 'Find central nodes', formula: 'CENTRALITY.CLOSENESS' },
                { id: 'eigenvector', name: 'Eigenvector', description: 'Find well-connected nodes', formula: 'CENTRALITY.EIGENVECTOR' }
            ]
        },
        {
            id: 'community', name: 'Community', icon: 'users', description: 'Detect clusters & groups',
            algorithms: [
                { id: 'louvain', name: 'Louvain', description: 'Detect communities', formula: 'COMMUNITY.LOUVAIN' },
                { id: 'label_propagation', name: 'Label Propagation', description: 'Fast clustering', formula: 'COMMUNITY.LABEL_PROPAGATION' },
                { id: 'wcc', name: 'WCC', description: 'Weakly connected components', formula: 'COMMUNITY.WCC' }
            ]
        },
        {
            id: 'path', name: 'Path Finding', icon: 'route', description: 'Find paths between nodes',
            algorithms: [
                { id: 'dijkstra', name: 'Dijkstra', description: 'Shortest path', formula: 'PATH.DIJKSTRA' },
                { id: 'bfs', name: 'BFS', description: 'Breadth-first search', formula: 'PATH.BFS' },
                { id: 'dfs', name: 'DFS', description: 'Depth-first search', formula: 'PATH.DFS' }
            ]
        },
        {
            id: 'similarity', name: 'Similarity', icon: 'git-compare', description: 'Find similar nodes',
            algorithms: [
                { id: 'jaccard', name: 'Jaccard', description: 'Neighborhood similarity', formula: 'SIMILARITY.JACCARD' },
                { id: 'cosine', name: 'Cosine', description: 'Attribute similarity', formula: 'SIMILARITY.COSINE' }
            ]
        },
        {
            id: 'embedding', name: 'Embedding', icon: 'box', description: 'Generate node vectors',
            algorithms: [
                { id: 'fastRP', name: 'FastRP', description: 'Fast random projection', formula: 'EMBEDDING.FASTRP' },
                { id: 'node2vec', name: 'Node2Vec', description: 'Random walk embeddings', formula: 'EMBEDDING.NODE2VEC' },
                { id: 'graphsage', name: 'GraphSAGE', description: 'Inductive embeddings', formula: 'EMBEDDING.GRAPHSAGE' }
            ]
        },
        {
            id: 'link_prediction', name: 'Link Prediction', icon: 'link', description: 'Predict missing links',
            algorithms: [
                { id: 'common_neighbors', name: 'Common Neighbors', description: 'Shared connections', formula: 'LINK.COMMON_NEIGHBORS' },
                { id: 'preferential_attachment', name: 'Preferential Attachment', description: 'Degree-based', formula: 'LINK.PREFERENTIAL_ATTACHMENT' }
            ]
        }
    ];

    // ============================================
    // Function Categories for Insights
    // ============================================
    const FUNCTION_CATEGORIES = [
        {
            id: 'insights', name: 'Insights', icon: 'sparkles',
            functions: [
                { 
                    id: 'main_topics', 
                    name: 'Identify Main Topics', 
                    description: 'Find the main themes and ideas in your graph',
                    formula: 'COMMUNITY.LOUVAIN + CENTRALITY.PAGERANK'
                },
                { 
                    id: 'influential', 
                    name: 'Influential Concepts', 
                    description: 'What are the most influential nodes?',
                    formula: 'CENTRALITY.PAGERANK + CENTRALITY.EIGENVECTOR'
                },
                { 
                    id: 'knowledge_hubs', 
                    name: 'Knowledge Hubs', 
                    description: 'Find nodes that connect many others',
                    formula: 'CENTRALITY.DEGREE + CENTRALITY.BETWEENNESS'
                }
            ]
        },
        {
            id: 'gap_analysis', name: 'Gap Analysis', icon: 'alert-triangle',
            functions: [
                { 
                    id: 'orphan', 
                    name: 'Orphan Nodes', 
                    description: 'Find isolated nodes with no connections',
                    formula: 'GRAPH.ORPHANS'
                },
                { 
                    id: 'topics_to_connect', 
                    name: 'Topics to Connect', 
                    description: 'Generate ideas to bridge structural gaps between clusters',
                    formula: 'COMMUNITY.LOUVAIN + PATH.DIJKSTRA'
                },
                { 
                    id: 'conceptual_gateways', 
                    name: 'Conceptual Gateways', 
                    description: 'Target nodes to integrate peripheral ideas into the core',
                    formula: 'CENTRALITY.BETWEENNESS + COMMUNITY.LOUVAIN'
                }
            ]
        }
    ];

    // ============================================
    // Color Palette
    // ============================================
    const COLOR_PALETTE = [
        '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
        '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe',
        '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000',
        '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080'
    ];

    // ============================================
    // Default Physics Options
    // ============================================
    const DEFAULT_PHYSICS_OPTIONS = {
        barnesHut: {
            gravitationalConstant: -2000,
            centralGravity: 0.3,
            springLength: 200,
            springConstant: 0.04,
            damping: 0.4,
            avoidOverlap: 0.1
        },
        repulsion: {
            nodeDistance: 100,
            centralGravity: 0.2,
            springLength: 200,
            springConstant: 0.05,
            damping: 0.09
        },
        forceAtlas2Based: {
            gravitationalConstant: -200,
            centralGravity: 0.01,
            springLength: 400,
            springConstant: 0.08,
            damping: 0.6,
            avoidOverlap: 0
        },
        hierarchicalRepulsion: {
            nodeDistance: 120,
            centralGravity: 0.0,
            springLength: 100,
            springConstant: 0.01,
            damping: 0.09
        }
    };

    // ============================================
    // Notebook UI mode – reduced toolbar for embedded notebook usage
    // ============================================
    const NOTEBOOK_LEFT_TOOLBAR = [
        {
            section: 'base',
            sectionClass: 'toolbar-base toolbar-graph-only',
            buttons: [
                { id: 'btn-create-bookmark', icon: 'bookmark-plus', label: 'Create bookmark', action: 'createBookmark' },
                { id: 'btn-clear', icon: 'eraser', label: 'Clear view', action: 'clearView' }
            ]
        },
        {
            section: 'windows-graph',
            sectionClass: 'toolbar-graph-only',
            buttons: [
                { id: 'btn-explorer', icon: 'text-search', label: 'Search & Find', shortcut: 'Ctrl+K', action: 'explorerWindow', toggle: true },
                { id: 'btn-aliases', icon: 'link-2', label: 'Aliases', shortcut: 'A', action: 'aliasesWindow', toggle: true },
                { id: 'btn-selection', icon: 'info', label: 'Selection & Operators', shortcut: 'S', action: 'selectionWindow', toggle: true },
                { id: 'btn-patterns', icon: 'shapes', label: 'Patterns', shortcut: 'Ctrl+Shift+P', action: 'patternsWindow', toggle: true },
                { id: 'btn-algorithms', icon: 'cpu', label: 'Algorithms', shortcut: 'K', action: 'algorithmsWindow', toggle: true },
                { id: 'btn-functions', icon: 'lightbulb', label: 'Functions', shortcut: 'N', action: 'functionsWindow', toggle: true }
            ]
        }
    ];

    const NOTEBOOK_RIGHT_TOOLBAR = [
        {
            section: 'layout',
            sectionClass: 'toolbar-section-layout',
            buttons: [
                { id: 'btn-physics', icon: 'zap', label: 'Physics', shortcut: 'P', action: 'physics', toggle: true, active: true },
                { id: 'btn-fit', icon: 'maximize-2', label: 'Fit to screen', shortcut: 'F', action: 'fit' },
                { id: 'btn-legend', icon: 'palette', label: 'Legend', shortcut: 'G', action: 'legendWindow', toggle: true },
                { id: 'btn-reload', icon: 'refresh-cw', label: 'Reload data', shortcut: 'Shift+R', action: 'reload' },
                { id: 'btn-export', icon: 'download', label: 'Export', shortcut: 'Ctrl+E', action: 'exportPanel' },
                { id: 'btn-settings', icon: 'sliders-horizontal', label: 'Settings', shortcut: 'C', action: 'settingsWindow', toggle: true },
                { id: 'btn-rebuild', icon: 'hammer', label: 'Rebuild graph', action: 'rebuild' }
            ]
        }
    ];

    const UI_MODE_PRESETS = {
        full: {},
        notebook: {
            show_cursor: false,
            show_tabs: false,
            show_bottom_bar: true,
            show_chat: false,
            show_timeline: false,
            show_fql: false,
            toolbar: {
                left: NOTEBOOK_LEFT_TOOLBAR,
                right: NOTEBOOK_RIGHT_TOOLBAR
            }
        }
    };

    // ============================================
    // Default Explorer Options
    // ============================================
    const DEFAULT_EXPLORER_OPTIONS = {
        // Theme & Display
        theme: 'dark',
        show_legend: true,
        show_fql: true,
        show_toolbar: true,
        show_tabs: true,
        show_assistant: true,
        show_chat: true,
        show_timeline: false,
        extension_mode: false,
        
        // Physics Configuration
        physics_enabled: true,
        physics_solver: 'forceAtlas2Based',
        physics_options: DEFAULT_PHYSICS_OPTIONS,
        stabilization: {
            enabled: true,
            iterations: 50,
            updateInterval: 100,
            fit: true
        },
        
        // Layout Configuration
        layout: null,
        hierarchical: {
            enabled: false,
            levelSeparation: 150,
            treeSpacing: 200,
            blockShifting: true,
            edgeMinimization: true,
            parentCentralization: true,
            direction: 'UD',
            sortMethod: 'hubsize'
        },
        
        // Edge Configuration
        edge_smooth: 'continuous',
        edge_arrows: true,
        edge_arrow_scale: 0.5,
        inherit_edge_colors: false,
        
        // Interaction Configuration
        hover: true,
        drag_nodes: true,
        drag_view: true,
        zoom_view: true,
        multiselect: true,
        selection_mode: 'single',
        hide_edges_on_drag: true,
        hide_nodes_on_drag: false,
        tooltip_delay: 200,
        
        // Node Configuration
        node_shape: 'dot',
        node_size: 16,
        node_border_width: 2,
        node_font_size: 14,
        neighborhood_highlight: true,
        
        // API Configuration
        api_endpoint: null,
        workspace_id: null,
        cursor_id: null,
        instance_id: null,
        
        // Clustering Configuration
        auto_cluster: true,
        cluster_threshold: 500,
        cluster_attribute: 'resource_name',
        min_cluster_size: 3,
        
        // Server Layout Configuration
        auto_layout: true,       // Auto-select server-side layout algorithm
        layout_algorithm: null   // Explicit layout: 'spring', 'kamada_kawai', 'spectral', etc.
    };

    // ============================================
    // Public API
    // ============================================
    const ExplorerConstants = {
        TABS,
        TOOLBAR_BUTTONS,
        WINDOW_CONFIGS,
        OPERATORS,
        ALGORITHM_CATEGORIES,
        FUNCTION_CATEGORIES,
        COLOR_PALETTE,
        DEFAULT_PHYSICS_OPTIONS,
        DEFAULT_EXPLORER_OPTIONS,
        UI_MODE_PRESETS,
        NOTEBOOK_LEFT_TOOLBAR,
        NOTEBOOK_RIGHT_TOOLBAR,
        
        // Helper functions
        getColorPalette: () => [...COLOR_PALETTE],
        getColorForIndex: (index) => COLOR_PALETTE[index % COLOR_PALETTE.length],
        getOperator: (id) => OPERATORS.find(op => op.id === id),
        getAlgorithm: (categoryId, algorithmId) => {
            const category = ALGORITHM_CATEGORIES.find(c => c.id === categoryId);
            return category?.algorithms.find(a => a.id === algorithmId);
        },
        getFunction: (categoryId, functionId) => {
            const category = FUNCTION_CATEGORIES.find(c => c.id === categoryId);
            return category?.functions.find(f => f.id === functionId);
        }
    };

    // Export to global scope
    global.ExplorerConstants = ExplorerConstants;

})(typeof window !== 'undefined' ? window : this);
